import {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    AxiosStatic,
    CreateAxiosDefaults,
    Method,
    RawAxiosRequestHeaders,
} from 'axios'
import { getSession } from 'next-auth/react'
import { buildMemoryStorage, setupCache, AxiosCacheInstance, CacheRequestConfig } from 'axios-cache-interceptor'

// Extended request config to include cache option
type RequestConfig = Pick<AxiosRequestConfig, 'headers' | 'params' | 'responseType'> & {
    useCache?: boolean
    cacheConfig?: {
        ttl?: number
        methods?: Lowercase<Method>[]
        cachePredicate?: {
            statusCheck?: (status: number) => boolean
        }
    }
}

export class AxiosClient {
    $axios: AxiosInstance
    axiosCacheInstance: AxiosCacheInstance
    storage: any
    onLogout: () => void = () => {}

    constructor(
        axios: AxiosStatic,
        config: CreateAxiosDefaults = {
            headers: {
                'X-Request-Source': 'powerapp-web',
            },
        },
        cacheOptions = {
            ttl: 15 * 60 * 1000, // Default cache time: 15 minutes
            methods: ['get'] as Lowercase<Method>[],
        },
    ) {
        // Create standard axios instance
        this.$axios = axios.create({
            ...config,
            withCredentials: true,
        })

        // Create memory storage for cache
        this.storage = buildMemoryStorage()

        // Setup cache with the axios instance - this returns an enhanced axios instance
        this.axiosCacheInstance = setupCache(
            axios.create({
                ...config,
                withCredentials: true,
            }),
            {
                ttl: cacheOptions.ttl,
                methods: cacheOptions.methods,
                storage: this.storage,
                debug: process.env.NODE_ENV !== 'production' ? () => {} : undefined,
                // Ensure the cache key generation is consistent
                generateKey: req => {
                    // Include the URL and any query params in the cache key
                    const url = new URL(req.url ?? '')
                    return `${req.method}:${url.pathname}:${url.search}`
                },
            },
        )

        // Apply interceptors to both instances
        this.initAuthTokenInterceptor(this.$axios)
        this.initAuthTokenInterceptor(this.axiosCacheInstance)

        this.initAutoLogoutInterceptor(this.$axios)
        this.initAutoLogoutInterceptor(this.axiosCacheInstance)
    }

    postFile(url: string, payload: FormData, config?: RequestConfig) {
        return this.$axios.post(url, payload, config)
    }

    get<T>(url: string, config?: RequestConfig) {
        const { useCache, cacheConfig, ...axiosConfig } = config ?? {}

        // If useCache is true, use the cache instance
        if (useCache) {
            const cacheRequestConfig: CacheRequestConfig = {
                ...axiosConfig,
                cache: {
                    // Override with provided cache config or use defaults
                    ttl: cacheConfig?.ttl,
                    methods: cacheConfig?.methods,
                    cachePredicate: {
                        statusCheck: cacheConfig?.cachePredicate?.statusCheck,
                    },
                    interpretHeader: false, // Don't use cache control headers
                    // Make sure to override: true to enable caching for this request
                    override: true,
                },
            }

            return this.axiosCacheInstance.get<T>(url, cacheRequestConfig)
        }

        // For non-cached requests, use the standard axios instance
        return this.$axios.get<T>(url, axiosConfig)
    }

    post<T>(url: string, payload?: Object, config?: RequestConfig) {
        return this.$axios.post<unknown, AxiosResponse<T>>(url, payload, config)
    }

    postForm<T>(url: string, payload?: Object, config?: RequestConfig) {
        return this.$axios.postForm<unknown, AxiosResponse<T>>(url, payload, config)
    }

    patch<T>(url: string, payload?: Object, config?: RequestConfig) {
        return this.$axios.patch<unknown, AxiosResponse<T>>(url, payload, config)
    }

    put<T>(url: string, payload?: Object, config?: RequestConfig) {
        return this.$axios.put<unknown, AxiosResponse<T>>(url, payload, config)
    }

    delete<T>(url: string, config?: RequestConfig) {
        return this.$axios.delete<unknown, T>(url, config)
    }

    // UPDATED: Get token from NextAuth session instead of cookies
    async initAuthTokenInterceptor(axiosInstance: AxiosInstance | AxiosCacheInstance) {
        axiosInstance.interceptors.request.use(
            async (config: any) => {
                const headers = config.headers as RawAxiosRequestHeaders

                // Check if Authorization header is already set
                if (!headers.Authorization) {
                    try {
                        // Get session from NextAuth
                        const session = await getSession()
                        if (session?.accessToken) {
                            headers.Authorization = `Bearer ${session.accessToken}`
                        }
                    } catch (error) {
                        console.error('Error getting session for auth token:', error)
                    }
                }
                return config
            },
            error => {
                return Promise.reject(new Error(error))
            },
        )
    }

    initAutoLogoutInterceptor(axiosInstance: AxiosInstance | AxiosCacheInstance) {
        axiosInstance.interceptors.response.use(
            (response: any) => {
                return response
            },
            async (error: AxiosError) => {
                if (error.response?.status === 401) {
                    this.onLogout()
                    return
                }

                return Promise.reject(error)
            },
        )
    }

    // Helper method to clear cache if needed
    clearCache() {
        if (this.storage && typeof this.storage.removeAll === 'function') {
            this.storage.removeAll()
        }
    }
}
